import {
  backgroundImageLinearGradient,
  marginBottom,
  marginTop,
  mq,
  paddingBottom,
  propToMarginV,
  propToPaddingV,
  stylesIfPropTrue,
} from '@sleepstation/style-api-helpers'
import { constrain } from '@sleepstation/style-mixins'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { css } from 'styled-components'
import Border from '../../Border'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const setMarginTop = (props) =>
  props.topMargin ? marginTop(props.topMargin) : ``

const setMarginBottom = (props) =>
  props.bottomMargin ? marginBottom(props.bottomMargin) : ``

const borderWrapperStyles = () => css`
  position: absolute;
  left: 0;
  right: 0;

  ${mq(`xSmallOnly`)} {
    ${stylesIfPropTrue(
      `hideBorderAtXSmall`,
      css`
        display: none;
      `
    )}
  }
`

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div`
  position: relative;

  ${propToMarginV(`marginV`)}
  ${setMarginTop}
  ${setMarginBottom}

  ${stylesIfPropTrue(
    `hasBottomBorder`,
    css`
      ${paddingBottom(1.5)}
    `
  )}

  ${mq(`xSmallOnly`)} {
    ${stylesIfPropTrue(
      `hideBorderAtXSmall`,
      css`
        padding-bottom: 0;
      `
    )}
  }

  ${(props) =>
    props.hasGradient &&
    css`
      ${backgroundImageLinearGradient()}
    `}

  ${(props) =>
    props.hasGradient &&
    props.hideBorderAtXSmall &&
    css`
      ${mq(`xSmallOnly`)} {
        background: none;
      }
    `}
`

const Content = styled.div`
  ${({ maxWidths }) => constrain(maxWidths)}
  ${/* sc-sel */ propToPaddingV(`paddingV`)}

  &:empty {
    display: none;
  }
`

const Inner = styled.div`
  ${stylesIfPropTrue(
    `isCropped`,
    css`
      overflow: hidden;
    `
  )}
`

const BorderWrapperBottom = styled.div`
  bottom: 0;
  ${borderWrapperStyles()}
`

const BorderWrapperTop = styled.div`
  top: 0;
  ${borderWrapperStyles()}
`

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const BreakoutModule = ({
  children,
  maxWidths,
  topMargin,
  bottomMargin,
  paddingV,
  marginV,
  hasGradient,
  hasBottomBorder,
  hasTopBorder,
  contentIsCropped,
  hideBorderAtXSmall,
}) => (
  <Layout
    className="Breakout"
    hasGradient={hasGradient}
    hasBottomBorder={hasBottomBorder}
    hasTopBorder={hasTopBorder}
    hideBorderAtXSmall={hideBorderAtXSmall}
    topMargin={topMargin}
    bottomMargin={bottomMargin}
    marginV={marginV}
  >
    <Inner isCropped={contentIsCropped}>
      <Content maxWidths={maxWidths} paddingV={paddingV}>
        {children}
      </Content>

      {hasTopBorder && (
        <BorderWrapperTop hideBorderAtXSmall={hideBorderAtXSmall}>
          <Border kind="outset" color="backgroundStart" edge="top" />
        </BorderWrapperTop>
      )}

      {hasBottomBorder && (
        <BorderWrapperBottom hideBorderAtXSmall={hideBorderAtXSmall}>
          <Border kind="inset" color="backgroundStart" edge="bottom" />
        </BorderWrapperBottom>
      )}
    </Inner>
  </Layout>
)

BreakoutModule.propTypes = {
  children: PropTypes.node,
  maxWidths: PropTypes.arrayOf(PropTypes.number).isRequired,
  topMargin: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  bottomMargin: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  paddingV: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  marginV: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  hasGradient: PropTypes.bool,
  hasBottomBorder: PropTypes.bool,
  hasTopBorder: PropTypes.bool,
  hideBorderAtXSmall: PropTypes.bool,
  contentIsCropped: PropTypes.bool,
}

BreakoutModule.defaultProps = {
  topMargin: null,
  children: null,
  bottomMargin: null,
  paddingV: 0,
  marginV: 0,
  hasGradient: false,
  hasBottomBorder: false,
  hasTopBorder: false,
  hideBorderAtXSmall: false,
  contentIsCropped: true,
}

export default BreakoutModule
